import { createRef, Ref, RefObject } from "react";
import { shutdown } from "@intercom/messenger-js-sdk";
import { useHistory, useRouteMatch } from "react-router";
import { Breakpoints, Colors, LoadingInterstitial } from "swing-components";

import { goToTop, MenuItemProps, Page, PageProps, useAuth } from "~components";
import {
  AUTH_URLS,
  createHelpUrl,
  createMoreUrl,
  createMyRequestsUrl,
  createProfileUrl,
  createReferralProgramUrl,
  createRequestsUrl,
} from "~pages";
import { translate } from "~utils";

import { Header } from "./Header";
import styles from "./ScorePage.module.css";

type ScorePageProps = Pick<PageProps, "children" | "isLoading"> & {
  title: string;
  hasBack?: boolean;
  hasTopBanner?: boolean;
  /**
   * When false, the fixed title/back header is not shown and content is not offset for it on small screens.
   * @default true
   */
  hasHeader?: boolean;
};

/**
 * Wrapper for the `Page` component that includes the onboarding navigation.
 *
 * TODO: @KoltonG - We could make this also take in route props to reduce the hooks usages.
 */
export function ScorePage(props: ScorePageProps) {
  const { title, isLoading = false, children, hasBack = false, hasHeader = true } = props;
  const ref: Ref<HTMLIonContentElement> = createRef();

  /***** Hooks *****/
  // Used to determine if we are on the proper page URL and if so highlight the
  // menu item.
  const openingsPageMatch = useRouteMatch(AUTH_URLS["requests"]);
  const myRequestsPageMatch = useRouteMatch(AUTH_URLS["myRequests"]);
  const morePageMatch = useRouteMatch(AUTH_URLS["more"]);
  const helpPageMatch = useRouteMatch(AUTH_URLS["help"]);
  const profilePageMatch = useRouteMatch(AUTH_URLS["profile"]);
  const referralProgramPageMatch = useRouteMatch(AUTH_URLS.referralProgram);

  // Used to navigate to different pages.
  const history = useHistory();
  const { signOut, userInfo } = useAuth();

  const isAliased = userInfo?.isAlias;

  /***** Render *****/
  // TODO: This approach is needed to conditionally render the desktop
  // `referral program` navigation but can remove this approach and
  // directly set these items for the `mobileNavigationProps` once referral
  // feature is released.
  function conditionalDesktopMenuItems(ref: RefObject<HTMLIonContentElement>) {
    const topMenuItems: MenuItemProps[] = [
      {
        iconName: "List",
        label: translate("NAV_OPENINGS"),
        isActive: !!openingsPageMatch?.isExact,
        onClick: async () => {
          if (openingsPageMatch?.isExact) {
            await goToTop(ref);
          } else {
            history.push(createRequestsUrl());
          }
        },
      },
      {
        iconName: "Calendar Check",
        label: translate("NAV_MY_REQUESTS"),
        isActive: !!myRequestsPageMatch?.isExact,
        onClick: async () => {
          if (myRequestsPageMatch?.isExact) {
            await goToTop(ref);
          } else {
            history.push(createMyRequestsUrl());
          }
        },
      },
    ];

    const bottomMenuItems: MenuItemProps[] = [
      {
        iconName: "Menu Meatball",
        label: translate("NAV_MORE"),
        isActive: !!morePageMatch?.isExact,
        onClick: async () => {
          if (morePageMatch?.isExact) {
            await goToTop(ref);
          } else {
            history.push(createMoreUrl());
          }
        },
      },
      {
        iconName: "Help",
        label: translate("MORE_HELP"),
        isActive: false,
        onClick: async () => {
          if (helpPageMatch?.isExact) {
            await goToTop(ref);
          } else {
            history.push(createHelpUrl());
          }
        },
      },
      {
        iconName: "Logout",
        label: translate("MORE_LOGOUT"),
        isActive: false,
        onClick: () => {
          // shutdown will be a noop if Intercom is not enabled
          shutdown();
          signOut({ history });
        },
      },
    ];

    if (!userInfo.restrictions?.includes("RESTRICTION_CANNOT_VIEW_REFERRAL")) {
      const referralProgramMenuItem: MenuItemProps = {
        iconName: "Group",
        label: translate("MORE_REFERRAL_PROGRAM"),
        isActive: !!referralProgramPageMatch?.isExact,
        onClick: async () => {
          if (referralProgramPageMatch?.isExact) {
            await goToTop(ref);
          } else {
            history.push(createReferralProgramUrl());
          }
        },
      };
      // Insert Referral Program before Logout
      bottomMenuItems.splice(bottomMenuItems.length - 1, 0, referralProgramMenuItem);
    }
    return { top: topMenuItems, bottom: bottomMenuItems };
  }

  return (
    <Page
      reference={ref}
      isAliased={isAliased}
      aliasEmail={userInfo.email}
      desktopNavigationProps={{
        menuItems: conditionalDesktopMenuItems(ref),
        profile: {
          name: userInfo.name,
          imageUrl: userInfo.imageUrl,
          isActive: profilePageMatch?.isExact,
          onClick: async () => {
            if (profilePageMatch?.isExact) {
              await goToTop(ref);
            } else {
              history.push(createProfileUrl());
            }
          },
        },
      }}
      mobileNavigationProps={{
        menuItems: [
          {
            iconName: "List",
            label: translate("NAV_OPENINGS"),
            isActive: !!openingsPageMatch?.isExact,
            onClick: async () => {
              if (openingsPageMatch?.isExact) {
                await goToTop(ref);
              } else {
                history.push(createRequestsUrl());
              }
            },
          },
          {
            iconName: "Calendar Check",
            label: translate("NAV_MY_REQUESTS"),
            isActive: !!myRequestsPageMatch?.isExact,
            onClick: async () => {
              if (myRequestsPageMatch?.isExact) {
                await goToTop(ref);
              } else {
                history.push(createMyRequestsUrl());
              }
            },
          },
          {
            iconName: "Person",
            label: translate("NAV_PROFILE"),
            isActive: !!profilePageMatch?.isExact,
            onClick: async () => {
              if (profilePageMatch?.isExact) {
                await goToTop(ref);
              } else {
                history.push(createProfileUrl());
              }
            },
          },
          {
            iconName: "Menu Meatball",
            label: translate("NAV_MORE"),
            isActive: !!morePageMatch?.isExact,
            onClick: async () => {
              if (morePageMatch?.isExact) {
                await goToTop(ref);
              } else {
                history.push(createMoreUrl());
              }
            },
          },
        ],
      }}
    >
      <div
        css={{
          color: Colors.black,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
        }}
      >
        {hasHeader && (
          <div className={styles["score-page-header-wrapper"]}>
            <Header title={title} hasBack={hasBack} />
          </div>
        )}
        {isLoading ? (
          <div
            css={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <LoadingInterstitial />
          </div>
        ) : (
          <div
            css={{
              marginTop: hasHeader ? "calc(72px + var(--ion-safe-area-top, 0))" : 0,
              [Breakpoints.mediumDeviceAndUp]: {
                marginTop: 0,
              },
            }}
          >
            {children}
          </div>
        )}
      </div>
    </Page>
  );
}
